import { getConcertGenre, matchesConcertGenre } from "@finda/shared/genres";
import type { Event } from "./types";
import { getEventGenres, groupRecurringEvents, isDisplayableGenre } from "./utils";
import { resolveJukeboxStart, type JukeboxStart } from "./jukebox-links";
import { getEventFeedItems } from "./event-feed";

export interface JukeboxArtist {
  attractionId: number;
  name: string;
  slug: string;
  imageUrl: string | null;
  spotifyId: string;
  genres?: string[];
  externalLinks?: Record<string, { url: string }[]> | null;
}

export interface JukeboxEvent {
  event: Event;
  artists: JukeboxArtist[];
  subtitle?: string;
  isPast?: boolean;
}

export function toJukeboxEvents(events: Event[], blockedSpotifyIds: string[] = []): JukeboxEvent[] {
  const blocked = new Set(blockedSpotifyIds);
  return events.flatMap((event) => {
    const artists = event.attractions.flatMap((artist) => !artist.spotify_id || blocked.has(artist.spotify_id) ? [] : [{
      attractionId: artist.id, name: artist.name, slug: artist.slug, imageUrl: artist.image_url,
      spotifyId: artist.spotify_id, genres: artist.genres, externalLinks: artist.external_links,
    }]);
    return artists.length ? [{ event, artists }] : [];
  });
}

/** Follow the feed's date groups and image/compact order when advancing from a card. */
export function feedJukeboxEvents(events: Event[], view: "list" | "grid", blockedSpotifyIds: string[] = [], gridImageOnly = false): JukeboxEvent[] {
  const ordered = getEventFeedItems(groupRecurringEvents(events), [], view, gridImageOnly, blockedSpotifyIds)
    .flatMap((item) => item.type === "event" ? item.group.events : []);
  return toJukeboxEvents(ordered, blockedSpotifyIds);
}

/** Open a bill from the feed inside the current playlist, adding it when the playlist lacks it. */
export function launchJukeboxAt(playlist: JukeboxEvent[], source: JukeboxEvent[], event: Event, artistId?: number, blockedSpotifyIds: string[] = []): JukeboxStart<JukeboxEvent> {
  const fallback = toJukeboxEvents([event], blockedSpotifyIds)[0] ?? null;
  // A card promises its whole playable bill, even when the genre playlist
  // originally kept only matching acts. Other bills keep their genre scope.
  const restoreBill = (entry: JukeboxEvent) => entry.event.id === event.id && fallback ? fallback : entry;
  return resolveJukeboxStart(playlist.map(restoreBill), source.map(restoreBill), { eventId: event.id, artistId: artistId ?? null }, fallback);
}

/** Where a launched session's last bill falls in the plain playlist, for resuming later. */
export function resumeIndexAfterLaunch(start: JukeboxStart<JukeboxEvent> | null, playlist: JukeboxEvent[], lastIndex: number): number {
  if (!start) return lastIndex;
  const id = start.playlist[lastIndex]?.event.id;
  const index = playlist.findIndex((entry) => entry.event.id === id);
  return index >= 0 ? index : Math.min(lastIndex, Math.max(0, playlist.length - 1));
}

/** Detail pages lead with this bill, then all genre matches, partial matches, and other bills. */
export function relatedJukeboxStart(events: Event[], event: Event, artistId?: number, blockedSpotifyIds: string[] = []): JukeboxStart<JukeboxEvent> {
  const current = toJukeboxEvents([event], blockedSpotifyIds)[0];
  const artist = current?.artists.find((artist) => artist.attractionId === artistId) ?? current?.artists[0];
  const genres = new Set((artist?.genres?.filter(isDisplayableGenre).length ? artist.genres : getEventGenres(event))
    .filter(isDisplayableGenre).map((genre) => genre.trim().toLowerCase()).filter(Boolean));
  const candidates = toJukeboxEvents(events.filter((candidate) => candidate.id !== event.id && candidate.status !== "cancelled"), blockedSpotifyIds);
  const matches = (tags: string[] = []) => new Set(tags.map((tag) => tag.trim().toLowerCase()).filter((tag) => genres.has(tag))).size;
  const ranked = candidates.map((entry) => {
    // Lead with the most similar playable act, including on a mixed-genre bill.
    const artists = [...entry.artists].sort((a, b) => matches(b.genres) - matches(a.genres));
    const count = matches(artists.flatMap((artist) => artist.genres ?? []));
    return { entry: { ...entry, artists }, count, tier: genres.size > 0 && count === genres.size ? 0 : count > 0 ? 1 : 2 };
  }).sort((a, b) => a.tier - b.tier || b.count - a.count
    || (a.entry.event.local_date ?? "9999").localeCompare(b.entry.event.local_date ?? "9999")
    || (a.entry.event.local_time ?? "99").localeCompare(b.entry.event.local_time ?? "99")
    || a.entry.event.id - b.entry.event.id);
  const playlist = dedupeCityJukeboxPlaylist([...(current ? [current] : []), ...ranked.map(({ entry }) => entry)]);
  return { playlist, index: 0, artistId: artist?.attractionId };
}

/** Keep the first recurring bill; lead with unheard artists on later bills. */
export function dedupeCityJukeboxPlaylist(playlist: JukeboxEvent[]): JukeboxEvent[] {
  const result: JukeboxEvent[] = [];
  const series = new Set<string>();
  const heard = new Set<string>();
  for (const entry of playlist) {
    const key = `${entry.artists.map((artist) => artist.spotifyId).sort().join(",")}::${entry.event.venue.id}`;
    if (series.has(key)) continue;
    series.add(key);
    const unseen = entry.artists.filter((artist) => !heard.has(artist.spotifyId));
    const artists = unseen.length ? [...unseen, ...entry.artists.filter((artist) => heard.has(artist.spotifyId))] : entry.artists;
    for (const artist of artists) heard.add(artist.spotifyId);
    result.push({ ...entry, artists });
  }
  return result;
}

/** Genre playlists preview each matching artist once across the entire calendar. */
export function genreJukeboxPlaylists(events: Event[], genreSlug: string, blockedSpotifyIds: string[] = []) {
  const genre = getConcertGenre(genreSlug);
  if (!genre) return { source: [], playlist: [] };
  const source = toJukeboxEvents(events, blockedSpotifyIds).flatMap((entry) => {
    const artists = entry.artists.filter((artist) => matchesConcertGenre({ ...entry.event, attractions: entry.event.attractions.filter((attraction) => attraction.id === artist.attractionId) }, genre));
    return artists.length ? [{ ...entry, artists }] : [];
  });
  const byId = new Map(source.map((entry) => [entry.event.id, entry]));
  const heard = new Set<string>();
  const playlist = groupRecurringEvents(source.map((entry) => entry.event)).flatMap((group) => {
    const entry = byId.get(group.events[0].id)!;
    const artists = entry.artists.filter((artist) => {
      if (heard.has(artist.spotifyId)) return false;
      heard.add(artist.spotifyId);
      return true;
    });
    return artists.length ? [{ ...entry, artists }] : [];
  });
  return { source, playlist };
}
